import { normalizeText } from "@/shared/utils/normalizeText";
import { defineTool, jsonResult, type WebMcpToolsContext } from "./shared";

/**
 * Tools de mídia: o player de vídeo e a galeria de fotos do ensaio.
 *
 * Diferente das outras, são registradas pelo COMPONENTE que desenha o player
 * ou a galeria (Videos, MakingOf e Lives, cada um com a sua versão de prévia,
 * e Photos.vue), como a open_help_topic do CollapseBox: só existem enquanto ele
 * está na tela. Mexem no estado dele por um "controle" que o próprio componente
 * monta, pelos mesmos caminhos dos botões da página — então valem as mesmas
 * regras de prévia, seção bloqueada e 4K. Nada aqui chama a API nem altera
 * dados.
 *
 * Nenhuma devolve a mídia: só nomes de parte, qualidade, posição e tempo.
 */

// ---------------------------------------------------------------- vídeo

/** A aba do ensaio onde está o player. */
export type TipoDoVideo = "video" | "making-of" | "live";

export interface EstadoDoVideo {
  /** Player de prévia (quem não tem acesso) em vez do vídeo completo. */
  previa: boolean;
  /** Índice da parte atual, a partir de 0. */
  parte: number;
  partes: Array<{ nome: string; temVideo: boolean }>;
  qualidade: string | null;
  qualidades: string[];
}

type ResultadoDoPlay = "ok" | "bloqueado" | "sem-player";

export type ResultadoDaEscolha =
  | "ok"
  | "parte-inexistente"
  | "parte-sem-video"
  | "qualidade-inexistente"
  | "qualidade-4k"
  | "sem-qualidade"
  | "so-previa";

/**
 * O que cada aba informa às tools. Play, pausa e tempo saem direto do Plyr,
 * igual nas três; parte e qualidade ficam com o componente, que é quem sabe
 * como a sua tela troca uma e outra.
 */
export interface ControleDoVideo {
  tipo: TipoDoVideo;
  /** O Plyr do player na tela, ou null enquanto ele não existe. */
  player(): any;
  estado(): EstadoDoVideo;
  escolherParte(indice: number): Promise<ResultadoDaEscolha>;
  escolherQualidade(nome: string): Promise<ResultadoDaEscolha>;
}

/**
 * Play num player do Plyr, traduzindo a recusa do browser. O play pedido por
 * um agente não conta como clique da pessoa, e o browser pode barrar o vídeo
 * com som (NotAllowedError) — aí quem resolve é o play na tela.
 */
async function darPlay(player: any): Promise<ResultadoDoPlay> {
  if (!player) return "sem-player";
  try {
    // A promise do play só resolve quando o vídeo começa; com o HLS ainda
    // carregando pode demorar. Passado o teto, o pedido já está feito.
    await Promise.race([
      Promise.resolve(player.play()),
      new Promise((resolve) => setTimeout(resolve, 4000)),
    ]);
    return "ok";
  } catch (erro: any) {
    return erro?.name === "NotAllowedError" ? "bloqueado" : "sem-player";
  }
}

/** Qualidade pelo nome que o agente mandou: igual, ou contida ("720p" → "HD 720p"). */
export function indiceDaQualidade(qualidades: string[], pedida: string): number {
  const alvo = normalizeText(pedida);
  const igual = qualidades.findIndex((nome) => normalizeText(nome) === alvo);
  if (igual >= 0 || !alvo) return igual;
  return qualidades.findIndex((nome) => normalizeText(nome).includes(alvo));
}

export function videoPlayerTools(
  ctx: WebMcpToolsContext,
  controle: ControleDoVideo
): WebMcpToolDescriptor[] {
  const { t } = ctx;
  const text = t.tools;

  // Quem fica na prévia muda de aba para aba: no vídeo, quem não assina; no
  // making of, quem não é Plus nem Gold; nas lives, quem não é Gold.
  const avisoDaPrevia: Record<TipoDoVideo, string> = {
    video: t.messages.videoPreviewOnly,
    "making-of": t.messages.makingOfPreviewOnly,
    live: t.messages.livePreviewOnly,
  };
  const recusaDoPlay: Record<Exclude<ResultadoDoPlay, "ok">, string> = {
    bloqueado: t.messages.autoplayBlocked,
    "sem-player": t.messages.noPlayer,
  };
  const recusaDaEscolha: Record<Exclude<ResultadoDaEscolha, "ok">, string> = {
    "parte-inexistente": t.messages.videoPartNotFound,
    "parte-sem-video": t.messages.videoPartWithoutVideo,
    "qualidade-inexistente": t.messages.videoQualityNotFound,
    "qualidade-4k": t.messages.videoQuality4k,
    "sem-qualidade": t.messages.liveWithoutQuality,
    "so-previa": avisoDaPrevia[controle.tipo],
  };

  function situacao() {
    const estado = controle.estado();
    const player = controle.player();
    const duracao = player ? Number(player.duration) : 0;
    return {
      content: controle.tipo,
      preview: estado.previa,
      playing: Boolean(player?.playing),
      second: player ? Math.round(Number(player.currentTime) || 0) : null,
      duration: duracao ? Math.round(duracao) : null,
      part: estado.parte + 1,
      parts: estado.partes.map((parte, i) => ({
        number: i + 1,
        name: parte.nome,
        hasVideo: parte.temVideo,
      })),
      quality: estado.qualidade,
      qualities: estado.qualidades,
      ...(estado.previa ? { note: avisoDaPrevia[controle.tipo] } : {}),
    };
  }

  return [
    defineTool(t, {
      name: "control_video",
      description: text.control_video.description,
      inputSchema: {
        type: "object",
        properties: {
          action: {
            type: "string",
            enum: ["play", "pause", "restart"],
            description: text.control_video.params.action,
          },
        },
        required: ["action"],
      },
      execute: async ({ action }) => {
        const player = controle.player();
        if (action === "pause") {
          player?.pause();
        } else if (action === "play" || action === "restart") {
          if (action === "restart" && player) player.currentTime = 0;
          const resultado = await darPlay(player);
          if (resultado !== "ok") return recusaDoPlay[resultado];
        }
        return jsonResult(situacao());
      },
    }),

    defineTool(t, {
      name: "choose_video",
      description: text.choose_video.description,
      inputSchema: {
        type: "object",
        properties: {
          part: {
            type: "number",
            minimum: 1,
            description: text.choose_video.params.part,
          },
          quality: {
            type: "string",
            description: text.choose_video.params.quality,
          },
        },
      },
      execute: async ({ part, quality }) => {
        if (part != null && part !== "") {
          const resultado = await controle.escolherParte(Number(part) - 1);
          if (resultado !== "ok") return recusaDaEscolha[resultado];
        }
        if (quality) {
          const resultado = await controle.escolherQualidade(String(quality));
          if (resultado !== "ok") return recusaDaEscolha[resultado];
        }
        return jsonResult(situacao());
      },
    }),
  ];
}

// ---------------------------------------------------------------- fotos

export interface EstadoDaGaleria {
  /** Índice da parte atual, a partir de 0. */
  parte: number;
  partes: string[];
  secao: string;
  /** Posição da foto na parte, a partir de 1, como o contador da página. */
  foto: number;
  totalNaParte: number;
  /** Parado no slide de "exclusivo para assinantes". */
  travada: boolean;
  slideshow: boolean;
  segundos: number;
}

export type ResultadoDaGaleria =
  | "ok"
  | "fim"
  | "inicio"
  | "travada"
  | "parte-inexistente";

export interface ControleDaGaleria {
  estado(): EstadoDaGaleria;
  proxima(): Promise<ResultadoDaGaleria>;
  anterior(): Promise<ResultadoDaGaleria>;
  irParaParte(indice: number): Promise<ResultadoDaGaleria>;
  slideshow(ligar: boolean, segundos?: number): Promise<void>;
}

/** O carrossel aceita de 1 a 10 segundos (CarrosselModel: minusPlay/plusPlay). */
export const SEGUNDOS_DO_SLIDESHOW = { minimo: 1, maximo: 10 } as const;

/**
 * Espera a tela mudar depois de um clique, até um teto. O carrossel troca de
 * foto em transição, e a troca de seção busca as fotos na API.
 */
export async function aguardarMudanca(
  ler: () => string,
  limiteMs = 1500
): Promise<void> {
  const antes = ler();
  const fim = Date.now() + limiteMs;
  while (Date.now() < fim) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    if (ler() !== antes) return;
  }
}

export function photoGalleryTools(
  ctx: WebMcpToolsContext,
  controle: ControleDaGaleria
): WebMcpToolDescriptor[] {
  const { t } = ctx;
  const text = t.tools;

  const recusa: Record<Exclude<ResultadoDaGaleria, "ok">, string> = {
    fim: t.messages.photoEnd,
    inicio: t.messages.photoStart,
    travada: t.messages.photoLocked,
    "parte-inexistente": t.messages.photoPartNotFound,
  };

  function situacao() {
    const estado = controle.estado();
    return {
      part: estado.parte + 1,
      partName: estado.partes[estado.parte] ?? null,
      parts: estado.partes.map((nome, i) => ({ number: i + 1, name: nome })),
      section: estado.secao,
      photo: estado.foto,
      of: estado.totalNaParte,
      ...(estado.travada ? { locked: true } : {}),
      slideshow: { on: estado.slideshow, seconds: estado.segundos },
    };
  }

  return [
    defineTool(t, {
      name: "navigate_photos",
      description: text.navigate_photos.description,
      inputSchema: {
        type: "object",
        properties: {
          action: {
            type: "string",
            enum: ["next", "previous", "part"],
            description: text.navigate_photos.params.action,
          },
          part: {
            type: "number",
            minimum: 1,
            description: text.navigate_photos.params.part,
          },
        },
        required: ["action"],
      },
      execute: async ({ action, part }) => {
        let resultado: ResultadoDaGaleria = "ok";
        if (action === "next") resultado = await controle.proxima();
        else if (action === "previous") resultado = await controle.anterior();
        else if (action === "part") {
          resultado = await controle.irParaParte(Number(part) - 1);
        }
        if (resultado !== "ok") return recusa[resultado];
        return jsonResult(situacao());
      },
    }),

    defineTool(t, {
      name: "photo_slideshow",
      description: text.photo_slideshow.description,
      inputSchema: {
        type: "object",
        properties: {
          action: {
            type: "string",
            enum: ["start", "stop"],
            description: text.photo_slideshow.params.action,
          },
          seconds: {
            type: "number",
            minimum: SEGUNDOS_DO_SLIDESHOW.minimo,
            maximum: SEGUNDOS_DO_SLIDESHOW.maximo,
            description: text.photo_slideshow.params.seconds,
          },
        },
        required: ["action"],
      },
      execute: async ({ action, seconds }) => {
        const segundos = Number(seconds);
        await controle.slideshow(
          action !== "stop",
          Number.isFinite(segundos) && segundos > 0
            ? Math.min(
                Math.max(Math.round(segundos), SEGUNDOS_DO_SLIDESHOW.minimo),
                SEGUNDOS_DO_SLIDESHOW.maximo
              )
            : undefined
        );
        return jsonResult(situacao());
      },
    }),
  ];
}
